import svgIcons from '@/assets/svg/icons';
import SvgIcon from '@/components/atoms/SvgIcon';
import useGetLatestNotifications from '@/hooks/notifications/useGetLatestNotifications';
import useGetUnreadNotificationsCount from '@/hooks/notifications/useGetUnreadNotificationsCount';
import colors from '@/theme/colors';
import {
  Center,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  ToastId,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import AllNotificationsDrawer from './components/AllNotificationsDrawer';
import NotificationListItem from './components/NotificationListItem';
import useReadMultipleNotifications from '@/hooks/notifications/useReadMultipleNotifications';
import NotificationsLoadingSkeleton from './components/NotificationsLoadingSkeleton';
import { NOTIFICATION_READ_DELAY_MILLISECONDS } from '@/constants';
import { useAppStore } from '@/store/useAppStore';
import NotificationItemDisplay from './components/NotificationItemDisplay';

const NOTIFICATION_TOAST_WIDTH = '370px';

const NotificationsBell = ({ color }: { color: string }) => {
  const { isOpen: isDropdownOpen, onOpen: onDropdownOpen, onClose: onDropdownClose } = useDisclosure();

  const { unreadNotifications: unreadNotificationsFromStore, setUnreadNotifications: setUnreadNotificationsToStore } =
    useAppStore();

  const {
    data: unreadCount,
    isLoading: isLoadingUnreadCount,
    refetch: refetchUnreadCount,
  } = useGetUnreadNotificationsCount();

  const {
    data: latestNotifications,
    isLoading: isLoadingLatestNotifications,
    refetch: refetchLatestNotifications,
  } = useGetLatestNotifications({
    enabled: !isLoadingUnreadCount,
  });

  useEffect(() => {
    if (isDropdownOpen) refetchLatestNotifications();
  }, [isDropdownOpen]);

  const latestNotificationToastRef = useRef<ToastId>();

  const [shouldOpenDrawer, setShouldOpenDrawer] = useState(false);

  const toast = useToast();

  const closeLatestNotificationToast = () => toast.close(latestNotificationToastRef.current ?? '');

  const createToastNotification = () =>
    (latestNotificationToastRef.current = toast({
      position: 'top-right',
      containerStyle: { width: NOTIFICATION_TOAST_WIDTH },
      render: () => (
        <VStack position={'relative'} width={'100%'} marginTop={'15px'} paddingRight={'20px'}>
          <VStack
            backgroundColor={'extra.white'}
            paddingX={'15px'}
            paddingY={'10px'}
            borderRadius={'10px'}
            width={'100%'}
            boxShadow={'0px 4px 10px 4px #00417933'}
          >
            <VStack
              position={'absolute'}
              top={'-15px'}
              right={'4px'}
              padding={'7px'}
              borderRadius={'50%'}
              backgroundColor={'extra.white'}
              cursor={'pointer'}
              onClick={closeLatestNotificationToast}
              boxShadow="0px 2px 4px 0px #7C9EBC80"
              _hover={{ boxShadow: '0px 1px 2px 0px #7C9EBC80' }}
            >
              <SvgIcon iconPath={svgIcons.closeSmall} color={colors.text.darkBlue} size={17} />
            </VStack>
            <NotificationItemDisplay
              title={'NEW NOTIFICATION'}
              timeStamp={'Just Now'}
              message={'A new notification has just arrived!'}
              isRead={false}
              CTALabel={'View Now'}
              onCTAClick={() => {
                setShouldOpenDrawer(true);
                closeLatestNotificationToast();
              }}
            />
          </VStack>
        </VStack>
      ),
    }));

  // comparing unreadCount from store to one we just fetched from BE
  useEffect(() => {
    if (unreadCount !== undefined) {
      if (unreadNotificationsFromStore !== undefined && unreadCount > unreadNotificationsFromStore) {
        createToastNotification();
        refetchLatestNotifications();
      }
      setUnreadNotificationsToStore(unreadCount);
    }
  }, [unreadCount, unreadNotificationsFromStore]);

  const latestNotificationsToDisplay = latestNotifications ?? [];

  const refetchCountAndLatestNotifications = () => {
    refetchUnreadCount();
    refetchLatestNotifications();
  };

  const { mutate: markMultipleNotificationsAsRead } = useReadMultipleNotifications(refetchCountAndLatestNotifications);

  const someLatestNotificationWithoutCTANotRead = latestNotificationsToDisplay.some(
    (notification) => !notification.action && !notification.isRead,
  );

  // upon opening the bell dropdown -> markMultipleNotificationsAsRead
  useEffect(() => {
    if (isDropdownOpen && someLatestNotificationWithoutCTANotRead) {
      const unreadLatestNotificationIds = latestNotificationsToDisplay
        .filter((notification) => !notification.action && !notification.isRead)
        .map((notification) => notification._id);

      if (unreadLatestNotificationIds.length > 0) {
        setTimeout(
          () => markMultipleNotificationsAsRead(unreadLatestNotificationIds),
          NOTIFICATION_READ_DELAY_MILLISECONDS,
        );
      }
    }
  }, [isDropdownOpen, someLatestNotificationWithoutCTANotRead, latestNotificationsToDisplay]);

  return (
    <Tooltip
      label="Notifications"
      visibility={isDropdownOpen ? 'hidden' : 'visible'}
      backgroundColor={'white'}
      borderRadius={'10px'}
      padding={'10px'}
      color={'text.mediumBlue'}
    >
      <HStack>
        <HStack paddingX={'10px'} paddingTop={'2px'} tabIndex={-1}>
          <Popover isOpen={isDropdownOpen} onOpen={onDropdownOpen} onClose={onDropdownClose} placement={'bottom-end'}>
            <PopoverTrigger>
              <VStack
                cursor={'pointer'}
                animation={isLoadingUnreadCount ? 'alternate-opacity 0.7s infinite ease-out alternate' : ''}
                position={'relative'}
              >
                {unreadCount && unreadCount > 0 && (
                  <Center
                    position={'absolute'}
                    top={'-20px'}
                    right={'-12px'}
                    backgroundColor={isDropdownOpen ? 'extra.veryLightRed' : 'extra.lightRed'}
                    fontSize={'10px'}
                    width={(unreadCount?.toString()?.length ?? 0 > 2) ? '28px' : '26px'}
                    maxHeight={(unreadCount?.toString()?.length ?? 0 > 2) ? '28px' : '26px'}
                    borderRadius={'50%'}
                    border={`3px solid ${colors.background.lightBlue}`}
                  >
                    <Text fontWeight={800} color={isDropdownOpen ? 'background.lightBlue' : 'extra.white'}>
                      {unreadCount}
                    </Text>
                  </Center>
                )}
                <SvgIcon
                  iconPath={svgIcons.bell}
                  size={24}
                  color={isDropdownOpen ? colors.extra.mediumBlue50pct : (color ?? colors.text.mediumBlue)}
                />
              </VStack>
            </PopoverTrigger>
            <PopoverContent
              bg="white"
              width={'350px'}
              borderRadius={'8px'}
              boxShadow={'0px 6px 12px 0px #0E006224'}
              top={'8px'}
              paddingX={'20px'}
              paddingTop={'5px'}
              tabIndex={-1}
            >
              {isLoadingLatestNotifications ? (
                <NotificationsLoadingSkeleton count={4} />
              ) : (
                latestNotificationsToDisplay.map((notification) => (
                  <NotificationListItem
                    key={notification._id}
                    notification={notification}
                    onCTAed={() => {
                      refetchCountAndLatestNotifications();
                      onDropdownClose();
                    }}
                  />
                ))
              )}

              {!isLoadingLatestNotifications && latestNotificationsToDisplay.length === 0 && (
                <Text variant={'urbanistSemiBold'} marginY={'30px'} color={'text.mediumBlue'}>
                  No Notifications to display.
                </Text>
              )}

              <AllNotificationsDrawer
                onDrawerClose={() => {
                  refetchCountAndLatestNotifications();

                  setShouldOpenDrawer(false);
                }}
                externalIsDrawerOpen={shouldOpenDrawer}
              />
            </PopoverContent>
          </Popover>
        </HStack>
      </HStack>
    </Tooltip>
  );
};

export default NotificationsBell;
